/**
 * AdminProspects — Pipeline CRM unifié (Sprint E).
 *
 * Fonctionnalités :
 * - 7 colonnes Kanban : Contacté → Relancé → Gagné → Perdu → Facturé → Payé → Onboardé
 * - Drag-and-drop simple via select (clic = change colonne, optionnel email auto)
 * - Fiche détail (modale) avec timeline + Convertir en membre
 * - Création manuelle
 */
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { langFlag, langLabel, LANG_OPTIONS } from "@/lib/langFlags";
import { POLES, poleLabel } from "@/lib/poles";
import { toast } from "sonner";
import BulkEmailModal from "@/components/admin/BulkEmailModal";
import {
  Plus, Mail, Trash2, X, Send, Loader2, ChevronRight, Building2,
  Phone, Crown, ExternalLink, FileText, History, Eye, LayoutGrid, List, Search, Users, ClipboardCheck,
} from "lucide-react";

const STATUSES = [
  { id: "new",          label: "Nouveau",     color: "bg-neutral-100 text-neutral-700", emoji: "📥" },
  { id: "contacted",    label: "Contacté",    color: "bg-blue-50 text-blue-700",        emoji: "📨" },
  { id: "qualified",    label: "Qualifié",    color: "bg-violet-50 text-violet-700",    emoji: "⭐" },
  { id: "candidature",  label: "Candidature", color: "bg-amber-100 text-amber-800",     emoji: "📝" },
  { id: "call_done",    label: "Appel fait",  color: "bg-purple-100 text-purple-800",   emoji: "📞" },
  { id: "proposal",     label: "Proposition", color: "bg-fuchsia-50 text-fuchsia-700",  emoji: "💼" },
  { id: "won",          label: "Gagné",       color: "bg-emerald-100 text-emerald-800", emoji: "✅" },
  { id: "lost",         label: "Perdu",       color: "bg-rose-100 text-rose-800",       emoji: "❌" },
  // Legacy terminal states — kept for backward compat
  { id: "invoiced",     label: "Facturé",     color: "bg-amber-100 text-amber-800",     emoji: "📄" },
  { id: "paid",         label: "Payé",        color: "bg-purple-100 text-purple-800",   emoji: "💰" },
  { id: "onboarded",    label: "Onboardé",    color: "bg-wine-100 text-wine-800",       emoji: "🎉" },
];

export default function AdminProspects() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [emailDlg, setEmailDlg] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("prospects_view") || "kanban");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showConverted, setShowConverted] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("prospects_view", v); };

  // Debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = () => api.get("/admin/prospects", {
    params: {
      include_converted: showConverted,
      archived: showArchived,
      search: debouncedSearch || undefined,
    },
  }).then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showConverted, showArchived, debouncedSearch]);

  const toggleSelect = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const changeStatus = async (pid, status, notify = true) => {
    try {
      await api.patch(`/admin/prospects/${pid}/status`, { status, notify });
      toast.success(notify ? "Statut mis à jour + email envoyé" : "Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const onDragStart = (e, pid) => { e.dataTransfer.setData("text/plain", pid); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e, colId) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(colId); };
  const onDragLeave = () => setDragOver(null);
  const onDrop = async (e, colId) => {
    e.preventDefault();
    setDragOver(null);
    const pid = e.dataTransfer.getData("text/plain");
    if (!pid) return;
    const prospect = list.find((p) => p.id === pid);
    if (!prospect || prospect.status === colId) return;
    const notify = window.confirm(`Changer le statut vers "${STATUSES.find((s) => s.id === colId)?.label}" et notifier ${prospect.contact_email || "le prospect"} par email ?\n\nOK = avec email · Annuler = sans email`);
    await changeStatus(pid, colId, notify);
  };

  const del = async (pid) => {
    if (!window.confirm("Supprimer définitivement ce prospect ?")) return;
    await api.delete(`/admin/prospects/${pid}`);
    toast.success("Supprimé");
    load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.size} prospect${selected.size > 1 ? "s" : ""} ?\n\nCette action est irréversible.`)) return;
    try {
      const r = await api.post("/admin/prospects/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data?.deleted || 0} prospect${(r.data?.deleted || 0) > 1 ? "s" : ""} supprimé${(r.data?.deleted || 0) > 1 ? "s" : ""}`);
      clearSelection();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const bulkConvertToCandidate = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Convertir ${selected.size} prospect${selected.size > 1 ? "s" : ""} en candidat${selected.size > 1 ? "s" : ""} ?\n\nUne candidature sera créée pour chacun (sans paiement, sans email). Les prospects déjà convertis seront ignorés.`)) return;
    try {
      const r = await api.post("/admin/prospects/bulk-convert-to-candidate", { ids: Array.from(selected) });
      const { converted, skipped, not_found } = r.data || {};
      toast.success(`${converted} candidature${converted > 1 ? "s" : ""} créée${converted > 1 ? "s" : ""}${skipped ? ` — ${skipped} déjà converti${skipped > 1 ? "s" : ""}` : ""}${not_found ? ` — ${not_found} introuvable${not_found > 1 ? "s" : ""}` : ""}`, { duration: 6000 });
      clearSelection();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const bulkArchive = async (archive = true) => {
    if (selected.size === 0) return;
    if (!window.confirm(`${archive ? "Archiver" : "Désarchiver"} ${selected.size} prospect${selected.size > 1 ? "s" : ""} ?`)) return;
    try {
      const r = await api.post("/admin/prospects/bulk-archive", { ids: Array.from(selected), archive });
      toast.success(`${r.data?.updated || 0} prospect${(r.data?.updated || 0) > 1 ? "s" : ""} ${archive ? "archivé" : "désarchivé"}${(r.data?.updated || 0) > 1 ? "s" : ""}`);
      clearSelection();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const grouped = STATUSES.map((s) => ({ ...s, items: list.filter((p) => (p.status || "contacted") === s.id) }));

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy md:text-4xl">Prospects</h1>
            <p className="mt-1 text-sm text-neutral-600">{list.length} prospect{list.length > 1 ? "s" : ""} · pipeline de A à Z {view === "kanban" && <span>· <strong className="text-wine-600">Glisser-déposer pour changer d&apos;étape</strong></span>}</p>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <>
                <button
                  onClick={() => setBulkEmailOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700"
                  data-testid="bulk-email-template-btn"
                  title="Envoyer un email avec template + langue auto"
                >
                  <Mail className="h-3 w-3" /> Email (template) ({selected.size})
                </button>
                <button
                  onClick={bulkConvertToCandidate}
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-white hover:bg-amber-600"
                  data-testid="bulk-convert-candidate-btn"
                  title="Créer des candidatures pour tous les prospects sélectionnés"
                >
                  📝 Convertir en candidats ({selected.size})
                </button>
                <button
                  onClick={() => bulkArchive(!showArchived)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-600 px-4 py-2 text-xs font-bold text-white hover:bg-slate-700"
                  data-testid="bulk-archive-btn"
                  title={showArchived ? "Désarchiver la sélection" : "Archiver la sélection"}
                >
                  {showArchived ? "↩️" : "📦"} {showArchived ? "Désarchiver" : "Archiver"} ({selected.size})
                </button>
                <button
                  onClick={bulkDelete}
                  className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3 w-3" /> Supprimer ({selected.size})
                </button>
                <button
                  onClick={clearSelection}
                  className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
                  data-testid="clear-selection-btn"
                >
                  ✕ Désélectionner
                </button>
              </>
            )}
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50">
              <input
                type="checkbox"
                checked={showConverted}
                onChange={(e) => setShowConverted(e.target.checked)}
                className="h-3 w-3 rounded border-neutral-300"
                data-testid="show-converted-toggle"
              />
              Voir membres convertis
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-3 w-3 rounded border-neutral-300"
                data-testid="show-archived-toggle"
              />
              📦 Archivés
            </label>
            <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1" data-testid="view-toggle">
              <button onClick={() => setViewPersist("kanban")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${view === "kanban" ? "bg-wine-500 text-white" : "text-neutral-600"}`} data-testid="view-kanban">
                <LayoutGrid className="h-3.5 w-3.5" /> Kanban
              </button>
              <button onClick={() => setViewPersist("list")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${view === "list" ? "bg-wine-500 text-white" : "text-neutral-600"}`} data-testid="view-list">
                <List className="h-3.5 w-3.5" /> Liste
              </button>
            </div>
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="new-prospect-btn">
              <Plus className="h-3.5 w-3.5" /> Nouveau prospect
            </button>
          </div>
        </div>

        {/* Search bar — Sprint 81 */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par entreprise, contact, email, SIREN, téléphone…"
            className="w-full rounded-full border border-neutral-200 bg-white pl-10 pr-10 py-2.5 text-sm placeholder:text-neutral-400 focus:border-wine-400 focus:outline-none focus:ring-2 focus:ring-wine-100"
            data-testid="prospects-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full text-neutral-400 hover:bg-neutral-100" title="Effacer">
              <X className="h-3 w-3" />
            </button>
          )}
          {showArchived && (
            <p className="mt-1.5 text-[11px] font-semibold text-slate-600">📦 Vous consultez les prospects archivés. Décochez «&nbsp;Archivés&nbsp;» pour revenir au pipeline actif.</p>
          )}
        </div>

        {/* Kanban view */}
        {view === "kanban" && (
        <div className="grid gap-3 overflow-x-auto pb-2 lg:grid-cols-7">
          {grouped.map((col) => (
            <div
              key={col.id}
              onDragOver={(e) => onDragOver(e, col.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, col.id)}
              className={`min-w-[220px] flex-shrink-0 rounded-2xl border p-2 transition-all ${dragOver === col.id ? "border-wine-500 bg-wine-50/60 ring-2 ring-wine-300" : "border-neutral-100 bg-neutral-50/40"}`}
              data-testid={`kanban-col-${col.id}`}
            >
              <div className={`mb-2 flex items-center justify-between rounded-lg px-2 py-1.5 text-[11px] font-bold ${col.color}`}>
                <span>{col.emoji} {col.label}</span>
                <span className="opacity-70">{col.items.length}</span>
              </div>
              <div className="space-y-2">
                {col.items.map((p) => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, p.id)}
                    onClick={() => setOpen(p)}
                    className={`cursor-grab w-full rounded-xl border bg-white p-2.5 text-left text-xs hover:shadow-sm active:cursor-grabbing ${selected.has(p.id) ? "border-emerald-500 ring-2 ring-emerald-300" : "border-neutral-200 hover:border-wine-300"}`}
                    data-testid={`prospect-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-neutral-900 truncate">{p.company_name || "—"}</div>
                        <div className="mt-0.5 text-[10px] text-neutral-500 line-clamp-1">{p.contact_name}{p.contact_email ? ` · ${p.contact_email}` : ""}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span title={langLabel(p.language) + " — auto depuis " + (p.country || "—")} className="text-base leading-none">{langFlag(p.language)}</span>
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={(e) => { e.stopPropagation(); toggleSelect(p.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-3 w-3 rounded border-neutral-300"
                          data-testid={`prospect-select-${p.id}`}
                        />
                      </div>
                    </div>
                    {p.pole && <div className="mt-1 inline-block rounded-full bg-wine-50 px-1.5 py-0.5 text-[9px] font-semibold text-wine-700">{poleLabel(p.pole)}</div>}
                    {(p.emails_sent || []).length > 0 && <div className="mt-1 text-[9px] text-neutral-400">{(p.emails_sent || []).length} email(s)</div>}
                  </div>
                ))}
                {col.items.length === 0 && (
                  <div className="rounded-lg border border-dashed border-neutral-200 p-3 text-center text-[10px] text-neutral-400">{dragOver === col.id ? "Déposer ici" : "Aucun"}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* List view */}
        {view === "list" && (
          <div className="rounded-2xl border border-neutral-100 bg-white">
            <div className="flex flex-wrap items-center gap-2 border-b border-neutral-100 p-3">
              <span className="text-xs font-semibold text-neutral-500">Filtrer :</span>
              <button onClick={() => setFilterStatus("all")} className={`rounded-full px-3 py-1 text-[11px] font-bold ${filterStatus === "all" ? "bg-wine-500 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`} data-testid="list-filter-all">Tous ({list.length})</button>
              {STATUSES.map((s) => {
                const n = list.filter((p) => (p.status || "contacted") === s.id).length;
                return (
                  <button key={s.id} onClick={() => setFilterStatus(s.id)} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${filterStatus === s.id ? s.color + " ring-2 ring-wine-400" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`} data-testid={`list-filter-${s.id}`}>
                    {s.emoji} {s.label} ({n})
                  </button>
                );
              })}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-wine-50/40 text-left text-[11px] uppercase tracking-wider text-neutral-500">
                  <tr><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Pôle</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Activité</th><th className="px-4 py-3 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {list.filter((p) => filterStatus === "all" || (p.status || "contacted") === filterStatus).length === 0 && (
                    <tr><td colSpan="7" className="py-10 text-center text-sm text-neutral-400">Aucun prospect dans cette étape.</td></tr>
                  )}
                  {list
                    .filter((p) => filterStatus === "all" || (p.status || "contacted") === filterStatus)
                    .map((p) => {
                      const st = STATUSES.find((s) => s.id === (p.status || "contacted")) || STATUSES[0];
                      return (
                        <tr key={p.id} className="border-t border-neutral-100 hover:bg-wine-50/30" data-testid={`prospect-row-${p.id}`}>
                          <td className="px-3 py-3"><input type="checkbox" className="h-3.5 w-3.5 accent-wine-500" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`select-${p.id}`} /></td>
                          <td className="px-4 py-3">
                            <button onClick={() => setOpen(p)} className="font-bold text-neutral-900 hover:text-wine-700">{p.company_name || "—"}</button>
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-700">
                            <div>{p.contact_name || "—"}</div>
                            <div className="text-[10px] text-neutral-500">{p.contact_email || ""}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-700">{p.pole ? poleLabel(p.pole) : "—"}</td>
                          <td className="px-4 py-3">
                            <select
                              value={p.status || "contacted"}
                              onChange={(e) => changeStatus(p.id, e.target.value, false)}
                              className={`cursor-pointer rounded-full border-0 px-2 py-1 text-[11px] font-bold ${st.color}`}
                              data-testid={`prospect-status-select-${p.id}`}
                            >
                              {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.emoji} {s.label}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-3 text-[10px] text-neutral-500">
                            {(p.emails_sent || []).length > 0 ? `${(p.emails_sent || []).length} email${(p.emails_sent || []).length > 1 ? "s" : ""}` : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex gap-1">
                              <button onClick={() => setOpen(p)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-600 hover:bg-neutral-100" title="Voir" data-testid={`prospect-view-${p.id}`}><Eye className="h-3.5 w-3.5" /></button>
                              <button onClick={() => setEmailDlg(p)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Email" data-testid={`prospect-email-${p.id}`}><Mail className="h-3.5 w-3.5" /></button>
                              <button onClick={() => del(p.id)} className="grid h-7 w-7 place-items-center rounded-full text-red-600 hover:bg-red-50" title="Supprimer" data-testid={`prospect-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {open && (
        <ProspectDetailModal
          prospect={open}
          onClose={() => setOpen(null)}
          onChange={() => { load(); setOpen(null); }}
          onChangeStatus={changeStatus}
          onDelete={del}
          onSendEmail={() => setEmailDlg(open)}
        />
      )}
      {creating && (
        <CreateProspectModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {emailDlg && (
        <EmailModal prospect={emailDlg} onClose={() => setEmailDlg(null)} onSent={() => { setEmailDlg(null); load(); }} />
      )}
      <BulkEmailModal
        open={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        entity="prospects"
        ids={Array.from(selected)}
        typeFilter="prospect"
        onSent={() => { clearSelection(); load(); }}
      />
    </PortalLayout>
  );
}

function CandidateSheetCard({ prospect, onChange }) {
  const [creating, setCreating] = useState(false);
  const appId = prospect.converted_application_id || prospect.migrated_from_application_id;

  const createSheet = async () => {
    if (!window.confirm("Créer la fiche candidature pour ce prospect ?\n\nCela ouvrira la fiche complète avec entretien, cotisation et lien Stripe (aucun email envoyé pour l'instant).")) return;
    setCreating(true);
    try {
      const r = await api.post(`/admin/prospects/${prospect.id}/convert-to-candidate`);
      toast.success("Fiche candidature créée");
      onChange?.();
      const newId = r.data?.application_id;
      if (newId) window.open(`/admin/applications/${newId}`, "_self");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/40 p-3.5" data-testid="candidate-sheet-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-white">
            <ClipboardCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-amber-900">Fiche candidature</div>
            <div className="mt-0.5 text-[11px] text-amber-800/80">
              {appId
                ? "Entretien · Cotisation · Lien Stripe · Facture — tout est dans la fiche dédiée."
                : "Ce prospect est en statut « Candidature » mais aucune fiche n'a encore été créée."}
            </div>
          </div>
        </div>
        {appId ? (
          <RouterLink
            to={`/admin/applications/${appId}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-amber-700"
            data-testid="open-candidate-sheet-btn"
          >
            Ouvrir la fiche <ExternalLink className="h-3 w-3" />
          </RouterLink>
        ) : (
          <button
            onClick={createSheet}
            disabled={creating}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-amber-700 disabled:opacity-50"
            data-testid="create-candidate-sheet-btn"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Créer la fiche
          </button>
        )}
      </div>
    </div>
  );
}

function ProspectDetailModal({ prospect, onClose, onChange, onChangeStatus, onDelete, onSendEmail }) {
  const [converting, setConverting] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState({ first_name: "", last_name: "", email: "", phone: "", job_title: "", is_primary: false });
  const [savingContact, setSavingContact] = useState(false);

  const loadContacts = () => {
    api.get(`/admin/prospects/${prospect.id}/contacts`)
      .then((r) => setContacts(r.data || []))
      .catch(() => setContacts([]));
  };
  useEffect(() => { loadContacts(); /* eslint-disable-next-line */ }, [prospect.id]);

  const addContact = async (e) => {
    e?.preventDefault();
    if (!contactForm.first_name && !contactForm.last_name && !contactForm.email) {
      toast.error("Prénom, nom ou email requis");
      return;
    }
    setSavingContact(true);
    try {
      await api.post(`/admin/prospects/${prospect.id}/contacts`, contactForm);
      toast.success("Contact ajouté");
      setContactForm({ first_name: "", last_name: "", email: "", phone: "", job_title: "", is_primary: false });
      setShowAddContact(false);
      loadContacts();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingContact(false); }
  };

  const removeContact = async (cid) => {
    if (!window.confirm("Supprimer ce contact ?")) return;
    try {
      await api.delete(`/admin/prospects/${prospect.id}/contacts/${cid}`);
      toast.success("Contact supprimé");
      loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const toggleArchive = async () => {
    const archive = !prospect.archived;
    if (!window.confirm(`${archive ? "Archiver" : "Désarchiver"} ce prospect ?`)) return;
    try {
      await api.post(`/admin/prospects/${prospect.id}/${archive ? "archive" : "unarchive"}`);
      toast.success(archive ? "Prospect archivé" : "Prospect désarchivé");
      onChange?.();
      onClose?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const convertToCandidate = async () => {
    if (!window.confirm(`Convertir ce prospect en CANDIDAT ?\n\nLe statut passera à "Candidature" (les candidatures sont fusionnées dans le pipeline Prospects).`)) return;
    try {
      await api.post(`/admin/prospects/${prospect.id}/convert-to-candidate`);
      toast.success("Prospect converti en candidat");
      onChange();
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prospect-detail-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
          <div>
            <h2 className="text-xl font-bold text-navy">{prospect.company_name}</h2>
            <div className="text-xs text-neutral-500">{prospect.contact_name} · {prospect.contact_email}</div>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 p-5">
          {/* Status selector */}
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Étape pipeline</label>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => onChangeStatus(prospect.id, s.id, true)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${prospect.status === s.id ? s.color + " ring-2 ring-wine-400" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
                  data-testid={`status-${s.id}`}
                >
                  {s.emoji} {s.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-neutral-500">Cliquer envoie automatiquement un email à {prospect.contact_email}</p>
          </div>

          {/* Sprint 86b — Candidate sheet shortcut (visible when prospect is a candidate) */}
          {(prospect.status === "candidature" || prospect.converted_application_id || prospect.migrated_from_application_id) && (
            <CandidateSheetCard prospect={prospect} onChange={onChange} />
          )}

          {/* Identity */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-start gap-2 text-xs"><Building2 className="mt-0.5 h-3.5 w-3.5 text-wine-500" /><div><div className="font-bold">{prospect.company_name}</div>{prospect.pole && <div className="text-neutral-500">{poleLabel(prospect.pole)}</div>}</div></div>
            <div className="flex items-start gap-2 text-xs"><Mail className="mt-0.5 h-3.5 w-3.5 text-wine-500" /><span>{prospect.contact_email || "—"}</span></div>
            <div className="flex items-start gap-2 text-xs"><Phone className="mt-0.5 h-3.5 w-3.5 text-wine-500" /><span>{prospect.contact_phone || "—"}</span></div>
            <div className="flex items-start gap-2 text-xs"><FileText className="mt-0.5 h-3.5 w-3.5 text-wine-500" /><span>Source : {prospect.source || "—"}</span></div>
          </div>

          {/* Linked contacts — Sprint 81 */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                <Users className="h-3 w-3" /> Contacts liés ({contacts.length})
              </div>
              <button onClick={() => setShowAddContact((s) => !s)} className="inline-flex items-center gap-1 rounded-full bg-wine-50 px-2.5 py-1 text-[10px] font-bold text-wine-700 hover:bg-wine-100" data-testid="prospect-add-contact-btn">
                <Plus className="h-2.5 w-2.5" /> {showAddContact ? "Annuler" : "Ajouter"}
              </button>
            </div>
            {showAddContact && (
              <form onSubmit={addContact} className="mt-2 grid gap-2 rounded-xl border border-wine-100 bg-wine-50/30 p-2.5 sm:grid-cols-2" data-testid="prospect-contact-form">
                <input value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} placeholder="Prénom" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" data-testid="prospect-contact-first" />
                <input value={contactForm.last_name} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} placeholder="Nom" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" data-testid="prospect-contact-last" />
                <input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" data-testid="prospect-contact-email" />
                <input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="Téléphone" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                <input value={contactForm.job_title} onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })} placeholder="Fonction" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs sm:col-span-2" />
                <label className="flex items-center gap-1.5 text-[10px] text-neutral-600 sm:col-span-2">
                  <input type="checkbox" checked={contactForm.is_primary} onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })} className="h-3 w-3" />
                  Contact principal (remplace celui par défaut)
                </label>
                <button type="submit" disabled={savingContact} className="rounded-full bg-wine-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-wine-700 sm:col-span-2 disabled:opacity-50" data-testid="prospect-contact-save">
                  {savingContact ? "…" : "Enregistrer le contact"}
                </button>
              </form>
            )}
            {contacts.length === 0 && !showAddContact && (
              <p className="mt-1 rounded-lg bg-neutral-50 p-2 text-[10px] text-neutral-500">Aucun contact lié. Ajoutez-en au moins un pour faciliter le suivi.</p>
            )}
            {contacts.length > 0 && (
              <div className="mt-1.5 space-y-1.5">
                {contacts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-2.5 py-1.5 text-[11px]">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-neutral-900 truncate">{c.contact_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}
                        {c.is_primary && <span className="ml-1.5 rounded bg-emerald-100 px-1 py-0.5 text-[9px] font-bold text-emerald-700">Principal</span>}
                      </div>
                      <div className="text-[10px] text-neutral-500 truncate">{c.email} {c.phone && `· ${c.phone}`} {c.job_title && `· ${c.job_title}`}</div>
                    </div>
                    <button onClick={() => removeContact(c.id)} className="grid h-6 w-6 place-items-center rounded-full text-rose-500 hover:bg-rose-50" title="Retirer le contact" data-testid={`prospect-contact-del-${c.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {prospect.notes && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Notes</div>
              <p className="mt-1 whitespace-pre-wrap rounded-lg bg-neutral-50 p-2.5 text-xs text-neutral-700">{prospect.notes}</p>
            </div>
          )}

          {/* History */}
          {(prospect.emails_sent || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500"><History className="h-3 w-3" /> Historique ({(prospect.emails_sent || []).length})</div>
              <div className="mt-1 max-h-32 overflow-y-auto space-y-1 rounded-lg bg-neutral-50 p-2">
                {(prospect.emails_sent || []).slice().reverse().map((e, i) => (
                  <div key={i} className="text-[10px] text-neutral-700"><strong>{e.type || "email"}</strong> — {e.sent_at ? new Date(e.sent_at).toLocaleString("fr") : ""} {e.subject ? `· ${e.subject}` : ""}</div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 border-t border-neutral-100 pt-4">
            {prospect.contact_email && (
              <button onClick={onSendEmail} className="inline-flex items-center gap-1.5 rounded-full bg-wine-50 px-3 py-1.5 text-xs font-bold text-wine-700 hover:bg-wine-100" data-testid="prospect-send-email-btn">
                <Send className="h-3 w-3" /> Email libre
              </button>
            )}
            <button onClick={() => convertToCandidate()} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600" data-testid="prospect-convert-candidate-btn" title="Créer une candidature (sans déclencher de paiement)">
              📝 Convertir en candidat
            </button>
            <button onClick={() => setConverting(true)} className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-purple-700" data-testid="prospect-convert-btn">
              <Crown className="h-3 w-3" /> Convertir en membre
            </button>
            <button onClick={toggleArchive} className="inline-flex items-center gap-1.5 rounded-full bg-slate-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-700" data-testid="prospect-archive-btn">
              {prospect.archived ? "↩️ Désarchiver" : "📦 Archiver"}
            </button>
            <button onClick={() => { onDelete(prospect.id); onClose(); }} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100">
              <Trash2 className="h-3 w-3" /> Supprimer
            </button>
          </div>
        </div>
      </div>

      {converting && (
        <ConvertProspectModal prospect={prospect} onClose={() => setConverting(false)} onSaved={() => { setConverting(false); onChange(); }} />
      )}
    </div>
  );
}

function ConvertProspectModal({ prospect, onClose, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category_id: "", pole: prospect.pole || "", bracket: 1, annual_revenue: "", final_amount: 0, payment_link: true, skip_email: false, custom_message: "" });
  const [autoCalc, setAutoCalc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/public/membership-pricing").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!form.category_id || !form.annual_revenue) { setAutoCalc(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/public/simulate-fee", { category_id: form.category_id, revenue: parseFloat(form.annual_revenue) || 0 });
        setAutoCalc(r.data);
        setForm((f) => ({ ...f, bracket: r.data.bracket, final_amount: r.data.fee }));
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.annual_revenue, form.category_id]);

  const convert = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, annual_revenue: form.annual_revenue ? parseFloat(form.annual_revenue) : null };
      const r = await api.post(`/admin/prospects/${prospect.id}/convert-to-member`, payload);
      setResult(r.data);
      toast.success("Conversion réussie");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="convert-prospect-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy"><Crown className="mr-1 inline h-5 w-5 text-purple-600" /> Convertir en membre</h3>
          <button onClick={onClose} className="grid h-7 w-7 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        {!result ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-neutral-50 p-2 text-xs">
              <strong>{prospect.company_name}</strong> · {prospect.contact_email}
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Catégorie *</label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="convert-prospect-category">
                <option value="">— Choisir —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">CA annuel déclaré (€)</label>
              <input type="number" value={form.annual_revenue} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })} placeholder="Ex: 250000" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="convert-prospect-revenue" />
              {autoCalc && <div className="mt-1 rounded-lg bg-emerald-50 p-2 text-xs text-emerald-900">✨ Suggestion : Tranche {autoCalc.bracket} ({autoCalc.bracket_label}) → <strong>{autoCalc.fee} €</strong></div>}
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Pôle</label>
              <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                <option value="">— Pôle —</option>
                {POLES.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.payment_link} onChange={(e) => setForm({ ...form, payment_link: e.target.checked })} />Inclure un lien Stripe</label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-bold text-neutral-700">Annuler</button>
              <button onClick={convert} disabled={submitting || !form.category_id} className="rounded-full bg-purple-600 px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="convert-prospect-submit">{submitting ? "…" : "Convertir"}</button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-emerald-50"><Crown className="h-6 w-6 text-emerald-600" /></div>
            <h4 className="font-bold text-emerald-700">Conversion réussie</h4>
            <p className="mt-1 text-xs">Application <code className="bg-neutral-100 px-1">{result.application_id?.slice(0, 8)}</code> pour <strong>{result.amount} €</strong></p>
            {result.payment_url && <a href={result.payment_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 rounded-full bg-purple-600 px-4 py-2 text-xs font-bold text-white">Lien Stripe <ExternalLink className="h-3 w-3" /></a>}
            <div className="mt-4"><button onClick={onSaved} className="rounded-full border border-neutral-300 px-4 py-1.5 text-xs font-bold">Fermer</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateProspectModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", contact_email: "", contact_phone: "",
    pole: "", notes: "",
    siren: "", vat_number: "", legal_form: "", naf_code: "", headcount_range: "",
    address: "", postal_code: "", city: "", country: "France", website: "",
  });
  const [saving, setSaving] = useState(false);
  // SIREN search state
  const [sirenQ, setSirenQ] = useState("");
  const [sirenSearching, setSirenSearching] = useState(false);
  const [sirenResults, setSirenResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Debounced SIREN/name search → recherche-entreprises.api.gouv.fr (via /api/public/insee/search)
  useEffect(() => {
    const q = sirenQ.trim();
    if (q.length < 2) { setSirenResults([]); setShowResults(false); return; }
    const t = setTimeout(async () => {
      setSirenSearching(true);
      try {
        const r = await api.get("/public/insee/search", { params: { q, limit: 8 } });
        setSirenResults(r.data?.results || []);
        setShowResults(true);
      } catch { setSirenResults([]); }
      finally { setSirenSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [sirenQ]);

  const pickCompany = (c) => {
    setForm((prev) => ({
      ...prev,
      company_name: c.denomination || prev.company_name,
      siren: c.siren || "",
      vat_number: c.vat_number || "",
      legal_form: c.legal_form || "",
      naf_code: c.naf_code || "",
      headcount_range: c.headcount_range || "",
      address: c.address || "",
      postal_code: c.postal_code || "",
      city: c.city || "",
      country: c.country || "France",
    }));
    setShowResults(false);
    setSirenQ("");
    toast.success(`Entreprise pré-remplie : ${c.denomination}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, status: "contacted", email: form.contact_email };
      await api.post("/admin/prospects", payload);
      toast.success("Prospect créé");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-prospect-modal">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-navy">Nouveau prospect</h3>
            <p className="text-xs text-neutral-500">Recherchez dans la base <strong>SIRENE</strong> (recherche-entreprises.api.gouv.fr) pour pré-remplir automatiquement.</p>
          </div>
          <button onClick={onClose} className="grid h-7 w-7 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        {/* SIREN / name search */}
        <div className="relative mb-4 rounded-xl border-2 border-dashed border-wine-200 bg-wine-50/30 p-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-wine-700">🔍 Rechercher dans la base SIRENE (par SIREN ou nom)</label>
          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={sirenQ}
              onChange={(e) => setSirenQ(e.target.value)}
              onFocus={() => sirenResults.length > 0 && setShowResults(true)}
              placeholder="Ex: 552120222 (SIREN) ou Vins de France (raison sociale)"
              className="w-full rounded-xl border border-wine-200 bg-white pl-10 pr-10 py-2 text-sm focus:border-wine-500 focus:outline-none focus:ring-2 focus:ring-wine-100"
              data-testid="prospect-siren-search"
            />
            {sirenSearching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-wine-500" />}
          </div>
          {showResults && sirenResults.length > 0 && (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-lg">
              {sirenResults.map((r) => (
                <button
                  key={r.siren}
                  type="button"
                  onClick={() => pickCompany(r)}
                  className="block w-full border-b border-neutral-100 px-3 py-2 text-left text-xs hover:bg-wine-50 last:border-b-0"
                  data-testid={`siren-result-${r.siren}`}
                >
                  <div className="font-bold text-neutral-900">{r.denomination}</div>
                  <div className="mt-0.5 text-[10px] text-neutral-500">
                    SIREN <strong className="font-mono">{r.siren}</strong>
                    {r.naf_label ? ` · ${r.naf_label}` : ""}
                    {r.city ? ` · ${r.city}` : ""}
                    {!r.is_active && <span className="ml-1 rounded bg-rose-100 px-1 py-0.5 text-rose-700">Fermée</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {showResults && sirenResults.length === 0 && !sirenSearching && sirenQ.length >= 2 && (
            <p className="mt-2 text-[11px] text-neutral-500">Aucun résultat — vous pouvez quand même remplir manuellement.</p>
          )}
        </div>

        {/* Manual form (pre-fillable from SIREN) */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Entreprise *</label>
            <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Nom de l'entreprise" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-prospect-company" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">SIREN</label>
            <input value={form.siren} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="9 chiffres" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="new-prospect-siren" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">TVA intra.</label>
            <input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="FRxx 123456789" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Contact principal</label>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Prénom Nom" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-prospect-contact-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Email</label>
            <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@exemple.fr" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-prospect-email" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Téléphone</label>
            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+33 …" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Site web</label>
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Pôle</label>
            <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
              <option value="">— Pôle —</option>
              {POLES.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Forme juridique</label>
            <input value={form.legal_form} onChange={(e) => setForm({ ...form, legal_form: e.target.value })} placeholder="SARL, SAS…" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Adresse</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, n°, complément" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Code postal</label>
            <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="75001" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-neutral-500">Ville</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Paris" className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase text-neutral-500">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes internes (admin)" rows={2} className="mt-0.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
          <button onClick={onClose} className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-bold">Annuler</button>
          <button onClick={save} disabled={saving || !form.company_name} className="rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="new-prospect-submit">{saving ? "…" : "Créer"}</button>
        </div>
      </div>
    </div>
  );
}

function EmailModal({ prospect, onClose, onSent }) {
  const [form, setForm] = useState({ subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!form.subject || !form.body) { toast.error("Sujet + corps requis"); return; }
    setSending(true);
    try { await api.post(`/admin/prospects/${prospect.id}/send-email`, form); toast.success("Email envoyé"); onSent(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-navy">Email à {prospect.contact_email}</h3>
          <button onClick={onClose} className="grid h-7 w-7 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-2">
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Sujet" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          <textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Message (HTML accepté)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" />
          <button onClick={send} disabled={sending} className="w-full rounded-full bg-wine-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{sending ? <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> : <Send className="mr-1 inline h-4 w-4" />}{sending ? "Envoi…" : "Envoyer"}</button>
        </div>
      </div>
    </div>
  );
}
